import {
  mdiAccount,
  mdiHome,
  mdiHomeCity,
  mdiMapMarker,
  mdiOfficeBuilding,
  mdiPhone,
} from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import { useOrderDetailCtx } from "../../../../../context_providers/order_detail_context";
import { AddressType } from "../../../../../utils/enums";
import { UpdateOrderDetailApi } from "../../../../../service/repos/order_repo";
import { fetchGet } from "../../../../../service/api_client";
import { CustomerSpocModel } from "../../../../../context_providers/auth/user_model";
import {
  ModalType,
  useModal,
} from "../../../../../context_providers/modal/modal_context";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import SelectAddressProfileWidget from "./select_address_profile_widget";
import { useAuth } from "../../../../../context_providers/auth/authProvider";

interface DeliveryProfileWidgetProps {}

const DeliveryProfileWidget: FunctionComponent<
  DeliveryProfileWidgetProps
> = () => {
  const { showModal, onClose } = useModal();
  const { showToast } = useToast();
  const { order, onCustomerSelect } = useOrderDetailCtx();
  const { user, hasRoleTitle } = useAuth();
  const [isEditAllowed, setIsEditAllowed] = useState(false);
  const [spocName, setSpocName] = useState("");

  const customerData = {
    name:
      (order?.customer?.firstName || order?.customer?.first_name || "") +
      " " +
      (order?.customer?.lastName || order?.customer?.last_name || ""),

    Branch:
      order?.delivery_profile?.branch_name ??
      order?.delivery_profile?.address?.branch_name ??
      "--",

    type: order?.customer?.delivery_address?.[0]?.type,
    mobile: order?.customer?.mobile ?? "--",
    alternateMobile: order?.customer?.alt_mobile ?? "",

    gst: order.customer?.gst ?? order.customer?.gst_details?.gst_number ?? "--",
    ca: order.customer.ca ?? "--",
    address:
      (order?.delivery_profile?.line1 ??
        order?.delivery_profile?.address?.line1 ??
        order?.customer?.delivery_address?.[0]?.line1 ??
        order?.customer?.address?.line1 ??
        "") +
      ", " +
      (order?.delivery_profile?.line2 ??
        order?.delivery_profile?.address?.line2 ??
        order?.customer?.delivery_address?.[0]?.line2 ??
        order?.customer?.address?.line2 ??
        "") +
      ", " +
      (order?.delivery_profile?.city ??
        order?.delivery_profile?.address?.city ??
        order?.customer?.delivery_address?.[0]?.city ??
        order?.customer?.address?.city ??
        "") +
      ", " +
      (order?.delivery_profile?.state ??
        order?.delivery_profile?.address?.state ??
        order?.customer?.delivery_address?.[0]?.state ??
        order?.customer?.address?.state ??
        "") +
      ", " +
      (order?.delivery_profile?.country ??
        order?.delivery_profile?.address?.country ??
        order?.customer?.delivery_address?.[0]?.country ??
        order?.customer?.address?.country ??
        "") +
      " - " +
      (order?.delivery_profile?.pincode ??
        order?.delivery_profile?.address?.pincode ??
        order?.customer?.delivery_address?.[0]?.pincode ??
        order?.customer?.address?.pincode ??
        ""),
  };

  useEffect(() => {
    const loggedInUser = user?.uid;
    const orderCreatedBy = order.created_by;
    const hasOrderCreatorAccess = hasRoleTitle({
      roleTitle: "Create orders only",
    });

    // Allow edit if the logged in user created the order, or has order creator role
    if (
      (loggedInUser === orderCreatedBy || hasOrderCreatorAccess) &&
      order?.update_restrict !== true
    ) {
      setIsEditAllowed(true);
    } else {
      setIsEditAllowed(false);
    }
  }, [user, order]);

  useEffect(() => {
    const normalizePhone = (phone?: string | number) =>
      String(phone ?? "")
        .replace(/\D/g, "")
        .slice(-10);

    const matchSpoc = (spocs?: CustomerSpocModel[]) => {
      const targetPhone = normalizePhone(customerData.mobile);
      if (!targetPhone || !spocs?.length) return "";
      const match = spocs.find(
        (spoc) =>
          normalizePhone(spoc.mobile_no_of_contact_person) === targetPhone,
      );
      return match?.name_of_contact_person ?? "";
    };

    const existingSpocs = (order?.customer as any)?.spocs as
      | CustomerSpocModel[]
      | undefined;

    if (existingSpocs?.length) {
      setSpocName(matchSpoc(existingSpocs));
      return;
    }

    const customerId = order?.customer_id || (order?.customer as any)?.uid;
    if (!customerId) {
      setSpocName("");
      return;
    }

    let cancelled = false;
    (async () => {
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/users/${customerId}`;
      const res = await fetchGet(url);
      if (!cancelled && res?.success) {
        setSpocName(matchSpoc(res.data?.spocs));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [order?.customer_id, order?.customer, customerData.mobile]);

  return (
    <div className="bg-white   rounded-md text-sm  h-full">
      <div className="p-5">
        <div className="flex justify-end">
          {isEditAllowed && (
            <button
              onClick={() => {
                showModal({
                  title: "Select Delivery Profile",
                  type: ModalType.modal,
                  container: (id) => (
                    <SelectAddressProfileWidget
                      type="Delivery"
                      user_id={order.user_id}
                      selected_id={order.customer?.id}
                      onAdded={async (data) => {
                        const deliveryAddress = {
                          branch_name: data.address.branch_name ?? "",
                          type: data.address.type ?? "Delivery",
                          line1: data.address.line1 ?? "",
                          line2: data.address.line2 ?? "",
                          line3: data.address.line3 ?? "",
                          city: data.address.city ?? "",
                          state: data.address.state ?? "",
                          country: data.address.country ?? "",
                          pincode: data.address.pincode ?? 0,
                        };

                        // 1. Update UI immedelivery
                        const updatedCustomer = {
                          ...order.customer,
                          delivery_address: [deliveryAddress],
                          id: data.id,
                        };

                        // 2. Call UpdateOrderDetailApi to persist this change in DB
                        await UpdateOrderDetailApi({
                          order_id: order?.order_id,
                          customer: updatedCustomer,
                        });

                        onCustomerSelect(updatedCustomer);

                        // 3. Close modal and show toast
                        onClose(id);
                        showToast({
                          type: ToastType.success,
                          text: "Delivery address updated in order",
                        });
                      }}
                    />
                  ),
                });
              }}
              className="cursor-pointer hover:scale-125 transform duration-300 text-myPrimaryColor font-semibold"
            >
              Edit
            </button>
          )}
        </div>
        <div className="">
          <div className="flex justify-between">
            <div className="">
              <div className="flex gap-1">
                <span>
                  <Icon
                    path={
                      customerData.type === AddressType.HOME
                        ? mdiHome
                        : customerData.type === AddressType.OFFICE
                          ? mdiOfficeBuilding
                          : mdiHomeCity
                    }
                    className="h-4 w-4 text-gray-500"
                  ></Icon>
                </span>
                <p>
                  <b>Delivery</b>
                </p>
              </div>
              <div className="flex gap-1">
                <span>
                  <Icon
                    path={mdiAccount}
                    className="h-4 w-4 text-gray-500"
                  ></Icon>
                </span>
                <p>
                  <b>{customerData.name}</b>
                </p>
              </div>
              <div className="flex gap-1">
                <Icon path={mdiPhone} className="h-4 w-4 text-gray-500"></Icon>

                <p>
                  {customerData.mobile}{" "}
                  {spocName && (
                    <span className="text-gray-500">({spocName})</span>
                  )}{" "}
                  {customerData.alternateMobile && (
                    <span>, {customerData.alternateMobile} </span>
                  )}
                </p>
              </div>
            </div>
            <div className="text-xs">
              <div>
                Branch: {order?.delivery_profile?.address?.branch_name ?? "--"}
              </div>
              <div className="flex gap-1">
                GST :<p>{customerData.gst}</p>
              </div>
              <div className="flex gap-1">
                CA :<p>{customerData.ca} </p>
              </div>
            </div>
          </div>
          <hr className="my-2" />
          <div className="flex gap-1">
            <Icon
              path={mdiMapMarker}
              size={1}
              className="  text-gray-500"
            ></Icon>
            <p className="text-xs">{customerData.address}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryProfileWidget;
